import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Facebook, Instagram, Linkedin, Twitter, Youtube } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/seo";

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, linkedin: Linkedin, twitter: Twitter, youtube: Youtube };

export default function AuthorPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/blog/authors/${slug}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [slug]);

  const author = data?.author;
  useSEO({ title: `${author?.name || "Author"} — Training Basket Blog`, description: author?.bio || "" });

  if (data === false) return <div className="max-w-3xl mx-auto px-6 py-24 text-center"><p className="font-bold text-xl">Author not found</p></div>;
  if (!data) return <div className="max-w-5xl mx-auto px-6 py-16"><div className="h-64 rounded-3xl bg-slate-100 animate-pulse" /></div>;

  const posts = data.posts || [];

  return (
    <div className="min-h-screen bg-white" data-testid="author-page">
      <section className="relative overflow-hidden border-b border-slate-100">
        <div className="absolute -right-24 -top-16 w-72 h-72 rounded-full bg-amber-100/60 blur-3xl" aria-hidden="true" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16 relative">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors" data-testid="author-back">
            <ArrowLeft className="w-4 h-4" /> All guides
          </Link>
          <div className="mt-8 rounded-3xl bg-slate-50 border border-slate-200 p-6 sm:p-10 flex flex-col sm:flex-row gap-6 sm:gap-8 items-start">
            {author.photo && (
              <img src={author.photo} alt={author.name} className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover border-4 border-white shadow-md shrink-0" data-testid="author-photo" />
            )}
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-amber-600">Author</p>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-1.5" data-testid="author-name">{author.name}</h1>
              <p className="text-sm sm:text-base text-slate-500 mt-3 leading-relaxed max-w-2xl" data-testid="author-bio">{author.bio}</p>
              {author.socials && (
                <div className="flex gap-2.5 mt-5" data-testid="author-socials">
                  {Object.entries(SOCIAL_ICONS).map(([key, Icon]) => author.socials[key] ? (
                    <a key={key} href={author.socials[key]} target="_blank" rel="noopener noreferrer" aria-label={key}
                      className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-[#FFC72C] hover:text-[#17263B] hover:border-[#FFC72C] transition-colors"
                      data-testid={`author-social-${key}`}>
                      <Icon className="w-4 h-4" />
                    </a>
                  ) : null)}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900" data-testid="author-posts-title">
          All posts by {author.name} <span className="text-slate-400 font-semibold text-base ml-2">({posts.length})</span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="author-posts-grid">
          {posts.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col" data-testid={`author-post-card-${p.slug}`}>
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                {p.image && <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
                {p.category && <span className="absolute top-3 left-3 rounded-full bg-[#FFC72C] text-[#17263B] text-[10px] font-extrabold uppercase tracking-wide px-3 py-1.5 shadow">{p.category}</span>}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-extrabold text-slate-900 leading-snug line-clamp-2">{p.title}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed line-clamp-2 flex-1">{p.excerpt}</p>
                <span className="inline-flex items-center gap-2 mt-4 text-sm font-bold text-[#17263B]">
                  Read More
                  <span className="w-7 h-7 rounded-full bg-[#FFC72C] group-hover:bg-[#F5B800] flex items-center justify-center transition-colors">
                    <ArrowRight className="w-3.5 h-3.5 text-[#17263B] group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
